import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { adminService } from '../../services/adminService';
import { Subscription } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  subscription: Subscription | null;
}

const ExtendPeriodModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, subscription }) => {
  const { t } = useTranslation();
  const [days, setDays] = useState('');
  const [extendTo, setExtendTo] = useState('');
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDays('');
      setExtendTo('');
      setReason('');
    }
  }, [isOpen, subscription?.id]);

  const handleSubmit = async () => {
    if (!subscription) return;
    if (!days && !extendTo) {
      toast.error(t('admin.extendDaysHint'));
      return;
    }
    setIsSaving(true);
    try {
      const payload: { extend_days?: number; extend_to?: string; reason?: string } = { reason: reason || undefined };
      if (extendTo) {
        // Send the date as-is ("YYYY-MM-DD"); backend interprets bare dates as
        // end-of-day UTC, avoiding the "Dec 1 became Nov 30 in BRT" bug.
        payload.extend_to = extendTo;
      } else if (days) {
        payload.extend_days = parseInt(days, 10);
      }
      const response = await adminService.extendSubscription(subscription.id, payload);
      if (response.success) {
        toast.success(t('admin.extendSuccess'));
        onClose();
        onSuccess?.();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('admin.extendPeriodTitle')} size="md">
      <div className="space-y-4">
        {subscription?.current_period_end && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('admin.nextBilling')}: <span className="font-medium text-gray-900 dark:text-white">
              {new Date(subscription.current_period_end).toLocaleDateString('pt-BR')}
            </span>
          </p>
        )}
        <Input
          label={t('admin.extendDays')}
          type="number"
          min="1"
          max="3650"
          value={days}
          onChange={(e) => { setDays(e.target.value); if (e.target.value) setExtendTo(''); }}
          helperText={t('admin.extendDaysHint') || ''}
          placeholder="30"
        />
        <Input
          label={t('admin.extendTo')}
          type="date"
          value={extendTo}
          onChange={(e) => { setExtendTo(e.target.value); if (e.target.value) setDays(''); }}
        />
        <Input
          label={t('admin.extendReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('admin.notesPlaceholder') || ''}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={isSaving || (!days && !extendTo)} loading={isSaving}>
            {t('admin.extendPeriod')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExtendPeriodModal;
